import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Smartphone } from 'lucide-react';

import {
  useAdminUserSessionsQuery,
} from '@/client/api/admin/users.query';
import {
  useRevokeSessionMutation,
  useRevokeAllSessionsMutation,
} from '@/client/api/admin/users.mutation';
import { useUserRoleAssignmentsQuery, useRoleTypesQuery } from '@/client/api/admin/roles';
import { useUserPermissionsQuery } from '@/client/api/admin/permissions';
import type { AdminUserListRow } from '@/client/types/admin/users-orgs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { KeyRound, ShieldPlus } from 'lucide-react';
import { useHrEmployeeByUidQuery } from '@/client/api/hr/people';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';

import {
  ManageRolesDialog,
  ManagePermissionsDialog,
} from '@/client/components/admin/UsersAndPermissions/UserRowActions';
import {
  describeSessionDevice,
  describeSessionLocation,
  describeSessionMeta,
  isUnrecognizedDevice,
} from '@/client/utils/admin-session-display';


type PendingAction =
  | { kind: 'revoke-all' }
  | { kind: 'revoke-session'; sid: string }
  | null;

const AccessTab = ({ row, authid }: { row: AdminUserListRow; authid: string | null }) => {
  const uid = row.user.uid;

  const { data: assignments = [], isLoading: rolesLoading } =
    useUserRoleAssignmentsQuery(authid, Boolean(authid));
  const { data: roleTypes = [] } = useRoleTypesQuery();
  const { data: userPerms = [], isLoading: permsLoading } = useUserPermissionsQuery(uid);
  const { data: sessionsData, isLoading: sessionsLoading } =
    useAdminUserSessionsQuery(authid, Boolean(authid));

  const revokeSession = useRevokeSessionMutation();
  const revokeAllSessions = useRevokeAllSessionsMutation();
  const [pending, setPending] = useState<PendingAction>(null);
  const [manage, setManage] = useState<'role' | 'permission' | null>(null);
  const hasConsole = row.user.admin === 1;
  // Console access and staff status are managed on the HR employee profile.
  const hrEmployee = useHrEmployeeByUidQuery(uid);

  const sessions = sessionsData?.sessions ?? [];
  const roleTypeById = useMemo(
    () => new Map(roleTypes.map(rt => [rt.uartid, rt])),
    [roleTypes]
  );

  const handleRevokeSession = async (sid: string) => {
    const toastId = toast.loading('Revoking session…');
    try {
      await revokeSession.mutateAsync({ authid: authid as string, sid });
      toast.success('Session revoked', { id: toastId, richColors: true });
    } catch (error: any) {
      toast.error('Failed to revoke session', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
      });
    }
  };

  const handleRevokeAll = async () => {
    const toastId = toast.loading('Revoking all sessions…');
    try {
      const result = await revokeAllSessions.mutateAsync({ authid: authid as string });
      toast.success('All sessions revoked', {
        id: toastId,
        description: `${result?.data?.revoked_count ?? 0} session(s) revoked.`,
        richColors: true,
      });
    } catch (error: any) {
      toast.error('Failed to revoke sessions', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
      });
    }
  };

  const runPending = async () => {
    if (!pending) return;
    const action = pending;
    setPending(null);
    if (action.kind === 'revoke-all') await handleRevokeAll();
    else if (action.kind === 'revoke-session') await handleRevokeSession(action.sid);
  };

  return (
    <>
      <section className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Console access</h2>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                hasConsole ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {hasConsole ? 'Can sign in' : 'No access'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Whether this account can sign in to the admin console. Granted and revoked from the
            person's HR profile; what they can do once inside comes from their roles.
          </p>
        </div>
        {hrEmployee.data ? (
          <Link
            to={`/admin/human-resource/people/directory/${hrEmployee.data.id}`}
            className="text-sm font-medium text-blue-700 hover:underline"
          >
            Manage in HR
          </Link>
        ) : (
          <Link to="/admin/human-resource/people/directory" className="text-sm font-medium text-blue-700 hover:underline">
            Add to the HR directory to manage
          </Link>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Roles</h2>
            <PermissionButton
              code={PERMISSIONS.role.create}
              variant="outline"
              size="sm"
              onClick={() => setManage('role')}
            >
              <ShieldPlus className="mr-1.5 h-4 w-4" />
              Manage roles
            </PermissionButton>
          </div>
          {rolesLoading ? (
            <div className="space-y-2">
              {[1, 2].map(i => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : assignments.length === 0 ? (
            <p className="py-4 text-center text-sm italic text-gray-400">No roles assigned.</p>
          ) : (
            <div className="space-y-2">
              {assignments.map(a => {
                const rt = a.user_account_role_type ?? roleTypeById.get(a.uartid);
                return (
                  <div
                    key={a.uarid}
                    className="flex items-start justify-between gap-3 rounded-md border bg-gray-50 p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {rt?.description ?? rt?.name ?? `Role ${a.uartid}`}
                      </p>
                      {(rt?.name || a.notes) && (
                        <p className="mt-0.5 text-xs text-gray-500">
                          {rt?.name}
                          {a.notes ? ` · ${a.notes}` : ''}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        a.active === 1
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {a.active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Direct permissions</h2>
            <PermissionButton
              code={PERMISSIONS.systemPermissions}
              variant="outline"
              size="sm"
              onClick={() => setManage('permission')}
            >
              <KeyRound className="mr-1.5 h-4 w-4" />
              Manage permissions
            </PermissionButton>
          </div>
          {permsLoading ? (
            <div className="space-y-2">
              {[1, 2].map(i => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : userPerms.length === 0 ? (
            <p className="py-4 text-center text-sm italic text-gray-400">
              No direct permissions.
            </p>
          ) : (
            <div className="space-y-2">
              {userPerms.map(p => (
                <div key={p.upermid} className="rounded-md border bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-900">{p.permission?.name}</p>
                  <p className="mt-0.5 font-mono text-xs text-gray-500">
                    {p.permission?.code}
                    {p.permission?.category ? ` · ${p.permission.category}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="mt-6 space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Active sessions</h2>
            <p className="mt-0.5 text-xs text-gray-400">
              {sessionsLoading
                ? 'Loading…'
                : `${sessions.length} session${sessions.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <PermissionButton
            code={PERMISSIONS.user.edit}
            variant="outline"
            size="sm"
            onClick={() => setPending({ kind: 'revoke-all' })}
            disabled={revokeAllSessions.isPending || sessions.length === 0}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            {revokeAllSessions.isPending ? 'Revoking…' : 'Revoke all'}
          </PermissionButton>
        </div>

        {sessionsLoading ? (
          <div className="space-y-2">
            {[1, 2].map(i => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <p className="py-4 text-center text-sm italic text-gray-400">No active sessions.</p>
        ) : (
          sessions.map(session => (
            <div
              key={session.sid}
              className="flex items-center justify-between gap-3 rounded-lg border bg-gray-50 p-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border bg-white shadow-sm">
                  <Smartphone className="h-4 w-4 text-gray-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {describeSessionDevice(session)}
                    </p>
                    {session.is_current && (
                      <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-700">
                        This device
                      </span>
                    )}
                    {isUnrecognizedDevice(session) && (
                      <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                        New device
                      </span>
                    )}
                  </div>
                  {describeSessionLocation(session) && (
                    <p className="text-xs text-gray-600">{describeSessionLocation(session)}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    {describeSessionMeta(session).join(' · ')}
                  </p>
                  <p className="truncate font-mono text-[11px] text-gray-400" title={session.sid}>
                    {session.sid}
                  </p>
                </div>
              </div>
              <PermissionButton
                code={PERMISSIONS.user.edit}
                variant="ghost"
                size="sm"
                onClick={() => setPending({ kind: 'revoke-session', sid: session.sid })}
                disabled={revokeSession.isPending}
                className="flex-shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                Revoke
              </PermissionButton>
            </div>
          ))
        )}
      </section>

      <ManageRolesDialog row={row} open={manage === 'role'} onOpenChange={o => !o && setManage(null)} />
      <ManagePermissionsDialog
        row={row}
        open={manage === 'permission'}
        onOpenChange={o => !o && setManage(null)}
      />

      <AlertDialog open={pending !== null} onOpenChange={o => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === 'revoke-all' ? 'Revoke all sessions?' : 'Revoke this session?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.kind === 'revoke-all'
                ? 'This signs the user out of every active session. They will need to sign in again.'
                : 'This signs the user out of the selected session immediately.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                runPending();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AccessTab;
